import { navigateTo, useNuxtApp } from "#app";
import type { Notification } from "~~/api/models";
import { NotificationActionType } from "~~/api/models";
import { useUserStore } from "~/store/user";
import {useI18n} from 'vue-i18n';

export const useNotificationStore = defineStore('notification', () => {
  /*
        State
     */
  const notifications = ref<Notification[]>([])
  const hasUnreadNotifications = ref<boolean>(false)

  /*
        Composable
     */
  const {t} = useI18n()
  const nuxtApp = useNuxtApp()
  const userStore = useUserStore()

  /*
        Watchers
     */
  watch(notifications, () => {
    // If the notifications array is not empty, and all the notifications are read, set the hasUnreadNotifications to false
    if (notifications.value.length > 0 && notifications.value.every(notification => notification.isRead))
      hasUnreadNotifications.value = false
  }, {immediate: false, deep: true})

  /*
        Actions
     */
  const getNotifications = async (read: boolean = false) => {
    notifications.value = await nuxtApp.$apiService.Notification.getNotifications(read)

    return notifications.value
  }

  const setHasUnreadNotifications = (value: boolean) => {
    hasUnreadNotifications.value = value
  }

  const markAllNotificationsAsRead = async () => {
    const cachedFlag = hasUnreadNotifications.value
    try {
      await nuxtApp.$apiService.Notification.markAllNotificationsAsRead()
      hasUnreadNotifications.value = false // Reset the hasUnreadNotifications flag
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
      hasUnreadNotifications.value = cachedFlag // Restore the hasUnreadNotifications flag
    }
  }

  const goToNotificationLink = async (notification: Notification) => {
    try {
      switch (notification.buttonActionType) {
        case NotificationActionType.GoToItem: {
          nuxtApp.$apiService.tenants = userStore.allSerialNumbers as string[];
          const itemWidgetResponse = await nuxtApp.$apiService.Item.findItemAndWidget(notification.entityId as string, null, 'id,tenantId');
          // Todo: If the item is not found, should we navigate to a 404 page?
          if (!itemWidgetResponse || !itemWidgetResponse?.item || !itemWidgetResponse?.widget)
            break;

          const item = itemWidgetResponse.item;
          const widget = itemWidgetResponse.widget;

          // Make sure the active organisation is the same as the one we're currently working in.
          await userStore.validateActiveOrganisation(String(item.tenantId));

          return await navigateTo({
            name: 'documents-widget-tenant-item',
            params: {widget: widget.normalizedName, item: item.id, tenant: item.tenantId},
          })
        }
        case NotificationActionType.GoToItems:
          // Todo: What should happen here?
          break;
        case NotificationActionType.GoToUrl: {
          if (notification.url) {
            const url = new URL(notification.url)
            const path = url.pathname

            return await navigateTo(path)
          }
          break;
        }
        default:
          // If the notification has a url. Just go to the url.
          if (notification.url) {
            const url = new URL(notification.url)
            const path = url.pathname

            return await navigateTo(path)
          }
          break;
      }
    } catch (error: unknown) {
      console.error('Error navigating to: ', notification.url, error)
    }
  }

  const translateString = (inputString: string) => {
    return inputString.replace(/#(.*?)#/g, (_, key) => t(key.toLowerCase()))
  }

  function transformParams(params: Record<string, string | number>): Record<string, string | number> {
    const regex = /#(.*?)#/g
    const result: Record<string, string | number> = {}

    for (const [key, value] of Object.entries(params)) {
      if (typeof value === 'string') {
        result[key] = value.replace(regex, (_, match) => t(match.toLowerCase()))
      } else {
        result[key] = value
      }
    }
    return result;
  }

  const getNotificationTranslation = (key: string, params: Record<string, string | number>, t: (key: string, params?: (Record<string, string | number> | null)) => string) => {
    // First, check if the key should be translated. If not, return the key as is.
    if (!key.includes('.')) return key;

    // Run through the params, if any of them is named "count" the value should be considered a number
    if (params?.["count"]) {
      params["count"] = parseInt(params["count"] as string);
    }

    const loweredKey = key.toLowerCase();
    const translatedParams = transformParams(params);
    let translation = t(loweredKey, translatedParams);

    if (translation === loweredKey) return key;

    // If the first Character is lowercase, capitalize it.
    if (translation.charAt(0).toLowerCase() === translation.charAt(0))
      translation = translation.charAt(0).toUpperCase() + translation.slice(1);

    return translation;
  }

  return {
    notifications,
    hasUnreadNotifications,
    getNotifications,
    setHasUnreadNotifications,
    markAllNotificationsAsRead,
    goToNotificationLink,
    getNotificationTranslation,
    translateString
  }
}, {
  persist: [
    {
      pick: ['hasUnreadNotifications'],
    }
  ]
})